import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { 
  FileText, Upload, Download, Trash2, Settings, Image, 
  File, Eye, Plus, RefreshCw, Calendar, User, Building2,
  Search, Filter, X, ChevronRight, FolderOpen, Clock
} from 'lucide-react';

const DOCUMENT_TYPES = {
  all: 'All Types',
  Notice: 'Notices',
  Claim: 'Claims',
  RFI: 'RFIs',
  'Site Instruction': 'Site Instructions',
  Other: 'Other'
};

const GROUP_BY_OPTIONS = {
  none: 'No Grouping',
  type: 'By Type',
  project: 'By Project',
  user: 'By User'
};

const SORT_OPTIONS = {
  newest: 'Newest First',
  oldest: 'Oldest First',
  name: 'By Name (A-Z)',
  name_desc: 'By Name (Z-A)',
  reference: 'By Reference',
  project: 'By Project'
};

const DocumentEngine = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('documents');
  const [letterheads, setLetterheads] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [groupBy, setGroupBy] = useState('type');
  
  // Projects and users for filtering
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    name: '',
    mode: 'header_only',
    apply_to: 'first_different',
    page_size: 'A4',
    margin_top: 25,
    margin_bottom: 20,
    margin_left: 20,
    margin_right: 20,
    header_height: 30,
    include_footer: true,
    is_default: false
  });
  const [uploadFile, setUploadFile] = useState(null);

  const loadLetterheads = useCallback(async () => {
    try {
      const response = await api.get('/documents/letterheads');
      setLetterheads(response.data);
    } catch (error) {
      toast.error('Failed to load letterheads');
    }
  }, []);

  const loadDocuments = useCallback(async () => {
    try {
      const response = await api.get('/documents/generated');
      setDocuments(response.data);
      
      // Extract unique users from documents
      const uniqueUsers = [];
      const userIds = new Set();
      response.data.forEach(doc => {
        if (doc.generated_by_id && !userIds.has(doc.generated_by_id)) {
          userIds.add(doc.generated_by_id);
          uniqueUsers.push({
            id: doc.generated_by_id,
            name: doc.generated_by_name || 'Unknown User'
          });
        }
      });
      setUsers(uniqueUsers);
    } catch (error) {
      toast.error('Failed to load documents');
    }
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      // Silently fail - projects filter will just be empty
      console.error('Failed to load projects:', error);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([loadLetterheads(), loadDocuments(), loadProjects()]);
      setLoading(false);
    };
    load();
  }, [loadLetterheads, loadDocuments, loadProjects]);

  const handleUploadLetterhead = async () => {
    if (!uploadFile || !uploadForm.name) {
      toast.error('Please provide a name and file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const params = new URLSearchParams({
        name: uploadForm.name,
        mode: uploadForm.mode,
        apply_to: uploadForm.apply_to,
        page_size: uploadForm.page_size,
        margin_top: uploadForm.margin_top.toString(),
        margin_bottom: uploadForm.margin_bottom.toString(),
        margin_left: uploadForm.margin_left.toString(),
        margin_right: uploadForm.margin_right.toString(),
        header_height: uploadForm.header_height.toString(),
        include_footer: uploadForm.include_footer.toString(),
        is_default: uploadForm.is_default.toString()
      });

      await api.post(`/documents/letterheads?${params}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Letterhead uploaded successfully');
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadForm({
        name: '',
        mode: 'header_only',
        apply_to: 'first_different',
        page_size: 'A4',
        margin_top: 25,
        margin_bottom: 20,
        margin_left: 20,
        margin_right: 20,
        header_height: 30,
        include_footer: true,
        is_default: false
      });
      loadLetterheads();
    } catch (error) {
      toast.error('Failed to upload letterhead');
    }
  };

  const handleDeleteLetterhead = async (id) => {
    if (!window.confirm('Are you sure you want to delete this letterhead?')) return;
    
    try {
      await api.delete(`/documents/letterheads/${id}`);
      toast.success('Letterhead deleted');
      loadLetterheads();
    } catch (error) {
      toast.error('Failed to delete letterhead');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await api.put(`/documents/letterheads/${id}`, { is_default: true });
      toast.success('Default letterhead updated');
      loadLetterheads();
    } catch (error) {
      toast.error('Failed to update default');
    }
  };

  const handleDownloadDocument = async (docId, format) => {
    try {
      const response = await api.get(`/documents/generated/${docId}/download/${format}`, {
        responseType: 'blob'
      });
      
      // Check if the response is actually an error JSON (not a blob)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const err = JSON.parse(text);
        toast.error(err.detail || `Failed to download ${format.toUpperCase()}`);
        return;
      }

      const contentDisposition = response.headers?.['content-disposition'];
      let filename = `document.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const detail = error.response?.data;
      let msg = `Failed to download ${format.toUpperCase()}`;
      if (detail instanceof Blob) {
        try {
          const text = await detail.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch {}
      } else if (typeof detail === 'object' && detail?.detail) {
        msg = detail.detail;
      }
      toast.error(msg);
    }
  };

  const handleDeleteDocument = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await api.delete(`/documents/generated/${id}`);
      toast.success('Document deleted');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Filter and sort documents
  const filteredDocuments = documents
    .filter(doc => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          doc.title?.toLowerCase().includes(query) ||
          doc.reference_number?.toLowerCase().includes(query) ||
          doc.document_type?.toLowerCase().includes(query) ||
          doc.project_name?.toLowerCase().includes(query) ||
          doc.generated_by_name?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Type filter
      if (typeFilter !== 'all' && doc.document_type !== typeFilter) return false;
      
      // Project filter
      if (projectFilter !== 'all' && doc.project_id !== projectFilter) return false;
      
      // User filter
      if (userFilter !== 'all' && doc.generated_by_id !== userFilter) return false;
      
      // Date filter
      if (dateFilter !== 'all') {
        const docDate = new Date(doc.generated_at);
        const now = new Date();
        if (dateFilter === 'today') {
          if (docDate.toDateString() !== now.toDateString()) return false;
        } else if (dateFilter === 'week') {
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          if (docDate < weekAgo) return false;
        } else if (dateFilter === 'month') {
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          if (docDate < monthAgo) return false;
        }
      }
      
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.generated_at) - new Date(a.generated_at);
      if (sortBy === 'oldest') return new Date(a.generated_at) - new Date(b.generated_at);
      if (sortBy === 'name') return (a.title || '').localeCompare(b.title || '');
      if (sortBy === 'name_desc') return (b.title || '').localeCompare(a.title || '');
      if (sortBy === 'reference') return (a.reference_number || '').localeCompare(b.reference_number || '');
      if (sortBy === 'project') return (a.project_name || '').localeCompare(b.project_name || '');
      return 0;
    });

  // Group documents based on selected grouping
  const groupedDocuments = filteredDocuments.reduce((acc, doc) => {
    let groupKey;
    let groupLabel;
    
    switch (groupBy) {
      case 'project':
        groupKey = doc.project_id || 'no-project';
        groupLabel = doc.project_name || 'No Project';
        break;
      case 'user':
        groupKey = doc.generated_by_id || 'unknown';
        groupLabel = doc.generated_by_name || 'Unknown User';
        break;
      case 'type':
        groupKey = doc.document_type || 'Other';
        groupLabel = doc.document_type || 'Other';
        break;
      default:
        groupKey = 'all';
        groupLabel = 'All Documents';
    }
    
    if (!acc[groupKey]) {
      acc[groupKey] = { label: groupLabel, docs: [] };
    }
    acc[groupKey].docs.push(doc);
    return acc;
  }, {});

  // Calculate stats
  const stats = {
    total: documents.length,
    byType: documents.reduce((acc, doc) => {
      const type = doc.document_type || 'Other';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {}),
    thisWeek: documents.filter(doc => {
      const docDate = new Date(doc.generated_at);
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      return docDate > weekAgo;
    }).length
  };

  const clearFilters = () => {
    setSearchQuery('');
    setTypeFilter('all');
    setProjectFilter('all');
    setUserFilter('all');
    setDateFilter('all');
    setSortBy('newest');
  };

  const hasActiveFilters = searchQuery || typeFilter !== 'all' || projectFilter !== 'all' || userFilter !== 'all' || dateFilter !== 'all';

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="document-engine-page">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-barlow font-bold uppercase tracking-tight text-slate-900">
              Document Engine
            </h1>
            <p className="text-slate-500 mt-1">Manage letterheads and generated professional documents</p>
          </div>
          <Button onClick={() => activeTab === 'letterheads' ? loadLetterheads() : loadDocuments()} variant="outline" className="rounded-sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Mobile: Dropdown */}
          <div className="sm:hidden mb-4">
            <Select value={activeTab} onValueChange={setActiveTab}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select view" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="documents">Generated Documents ({documents.length})</SelectItem>
                <SelectItem value="letterheads">Letterheads ({letterheads.length})</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Desktop: Tab List */}
          <TabsList className="bg-slate-100 rounded-sm hidden sm:inline-flex">
            <TabsTrigger value="documents" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-documents">
              <FileText className="h-4 w-4 mr-2" />
              Generated Documents ({documents.length})
            </TabsTrigger>
            <TabsTrigger value="letterheads" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-letterheads">
              <Image className="h-4 w-4 mr-2" />
              Letterheads ({letterheads.length})
            </TabsTrigger>
          </TabsList>

          {/* Generated Documents Tab */}
          <TabsContent value="documents" className="mt-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="border-slate-200 rounded-sm">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                  <p className="text-xs text-slate-500 uppercase font-medium">Total Documents</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200 rounded-sm">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold text-blue-600">{stats.thisWeek}</p>
                  <p className="text-xs text-slate-500 uppercase font-medium">This Week</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200 rounded-sm">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold text-purple-600">{stats.byType['Notice'] || 0}</p>
                  <p className="text-xs text-slate-500 uppercase font-medium">Notices</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200 rounded-sm">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold text-amber-600">{stats.byType['Claim'] || 0}</p>
                  <p className="text-xs text-slate-500 uppercase font-medium">Claims</p>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <div className="space-y-4 mb-6 bg-slate-50 p-4 rounded-lg border">
              {/* First row - Search and main filters */}
              <div className="flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by title, reference, project, or user..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 rounded-sm"
                    data-testid="document-search"
                  />
                </div>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-40 rounded-sm" data-testid="type-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(DOCUMENT_TYPES).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={projectFilter} onValueChange={setProjectFilter}>
                  <SelectTrigger className="w-44 rounded-sm" data-testid="project-filter">
                    <SelectValue placeholder="All Projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={userFilter} onValueChange={setUserFilter}>
                  <SelectTrigger className="w-44 rounded-sm" data-testid="user-filter">
                    <SelectValue placeholder="All Users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Second row - Date, Sort, Group, Clear */}
              <div className="flex flex-wrap gap-3 items-center border-t pt-3">
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger className="w-36 rounded-sm" data-testid="date-filter">
                    <SelectValue placeholder="All Time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="week">This Week</SelectItem>
                    <SelectItem value="month">This Month</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-40 rounded-sm" data-testid="sort-filter">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SORT_OPTIONS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2 ml-auto">
                  <span className="text-xs text-slate-500 uppercase font-medium">Group by:</span>
                  <Select value={groupBy} onValueChange={setGroupBy}>
                    <SelectTrigger className="w-36 rounded-sm" data-testid="group-filter">
                      <SelectValue placeholder="Group by" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(GROUP_BY_OPTIONS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {hasActiveFilters && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearFilters}
                    className="text-slate-500"
                  >
                    <X className="h-4 w-4 mr-1" /> Clear Filters
                  </Button>
                )}
              </div>
            </div>

            {/* Results Count */}
            {hasActiveFilters && (
              <div className="mb-4 text-sm text-slate-600">
                Showing {filteredDocuments.length} of {documents.length} documents
              </div>
            )}

            {loading ? (
              <div className="text-center py-12 text-slate-400">Loading...</div>
            ) : filteredDocuments.length === 0 ? (
              <Card className="border-slate-200 rounded-sm">
                <CardContent className="py-12 text-center">
                  <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">
                    {hasActiveFilters ? 'No documents match your filters' : 'No documents generated yet'}
                  </p>
                  <p className="text-xs text-slate-400 mt-2">
                    {hasActiveFilters ? 'Try adjusting your filters' : 'Documents are generated from Notices, Claims, and RFIs'}
                  </p>
                  {hasActiveFilters && (
                    <Button variant="outline" onClick={clearFilters} className="mt-4 rounded-sm">
                      Clear Filters
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Grouped Documents */}
                {groupBy === 'none' ? (
                  // No grouping - flat list
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredDocuments.map((doc) => (
                      <Card key={doc.id} className="border-slate-200 rounded-sm hover:shadow-md transition-shadow group" data-testid={`document-card-${doc.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono font-semibold text-slate-900 truncate">{doc.reference_number}</span>
                                <Badge variant="outline" className="text-[10px] shrink-0">{doc.document_type}</Badge>
                              </div>
                              <p className="text-sm text-slate-600 line-clamp-2">{doc.title}</p>
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400 mb-3">
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {doc.generated_by_name || 'Unknown'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatShortDate(doc.generated_at)}
                            </span>
                            {doc.project_name && (
                              <span className="flex items-center gap-1">
                                <Building2 className="h-3 w-3" />
                                {doc.project_name}
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                            <Badge variant="outline" className="rounded-sm text-[10px]">{doc.page_count || 0} pages</Badge>
                            {doc.annexure_count > 0 && (
                              <Badge variant="outline" className="rounded-sm text-[10px]">{doc.annexure_count} annexures</Badge>
                            )}
                          </div>

                          <div className="flex gap-2 pt-2 border-t">
                            {doc.has_pdf && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1 rounded-sm text-xs"
                                onClick={() => handleDownloadDocument(doc.id, 'pdf')}
                                data-testid={`download-pdf-${doc.id}`}
                              >
                                <Download className="h-3 w-3 mr-1" />
                                PDF
                              </Button>
                            )}
                            {doc.has_docx && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1 rounded-sm text-xs"
                                onClick={() => handleDownloadDocument(doc.id, 'docx')}
                                data-testid={`download-docx-${doc.id}`}
                              >
                                <Download className="h-3 w-3 mr-1" />
                                DOCX
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:bg-red-50 rounded-sm"
                              onClick={() => handleDeleteDocument(doc.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  // Grouped view
                  Object.entries(groupedDocuments).map(([groupKey, group]) => (
                    <div key={groupKey} className="space-y-3">
                      <div className="flex items-center gap-2 sticky top-0 bg-white/95 backdrop-blur-sm py-2 z-10">
                        <FolderOpen className="h-4 w-4 text-slate-400" />
                        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-600">{group.label}</h3>
                        <Badge variant="secondary" className="text-xs">{group.docs.length}</Badge>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {group.docs.map((doc) => (
                          <Card key={doc.id} className="border-slate-200 rounded-sm hover:shadow-md transition-shadow group" data-testid={`document-card-${doc.id}`}>
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-mono font-semibold text-slate-900 truncate">{doc.reference_number}</span>
                                    {groupBy !== 'type' && (
                                      <Badge variant="outline" className="text-[10px] shrink-0">{doc.document_type}</Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-slate-600 line-clamp-2">{doc.title}</p>
                                </div>
                              </div>
                              
                              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400 mb-3">
                                {groupBy !== 'user' && (
                                  <span className="flex items-center gap-1">
                                    <User className="h-3 w-3" />
                                    {doc.generated_by_name || 'Unknown'}
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {formatShortDate(doc.generated_at)}
                                </span>
                                {groupBy !== 'project' && doc.project_name && (
                                  <span className="flex items-center gap-1">
                                    <Building2 className="h-3 w-3" />
                                    {doc.project_name}
                                  </span>
                                )}
                              </div>
                              
                              <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                                <Badge variant="outline" className="rounded-sm text-[10px]">{doc.page_count || 0} pages</Badge>
                                {doc.annexure_count > 0 && (
                                  <Badge variant="outline" className="rounded-sm text-[10px]">{doc.annexure_count} annexures</Badge>
                                )}
                              </div>

                              <div className="flex gap-2 pt-2 border-t">
                                {doc.has_pdf && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 rounded-sm text-xs"
                                    onClick={() => handleDownloadDocument(doc.id, 'pdf')}
                                    data-testid={`download-pdf-${doc.id}`}
                                  >
                                    <Download className="h-3 w-3 mr-1" />
                                    PDF
                                  </Button>
                                )}
                                {doc.has_docx && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 rounded-sm text-xs"
                                    onClick={() => handleDownloadDocument(doc.id, 'docx')}
                                    data-testid={`download-docx-${doc.id}`}
                                  >
                                    <Download className="h-3 w-3 mr-1" />
                                    DOCX
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:bg-red-50 rounded-sm"
                                  onClick={() => handleDeleteDocument(doc.id)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </TabsContent>

          {/* Letterheads Tab */}
          <TabsContent value="letterheads" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold uppercase tracking-wider text-slate-700">
                Letterhead Templates
              </h2>
              <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-sm bg-blue-600 hover:bg-blue-700" data-testid="upload-letterhead-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Upload Letterhead
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-barlow font-bold uppercase">Upload Letterhead</DialogTitle>
                    <DialogDescription>
                      Upload a PNG, JPG, or PDF file to use as your letterhead template.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Name *</Label>
                      <Input
                        value={uploadForm.name}
                        onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                        placeholder="e.g., Company Standard Letterhead"
                        className="rounded-sm mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">File *</Label>
                      <Input
                        type="file"
                        accept=".png,.jpg,.jpeg,.pdf"
                        onChange={(e) => setUploadFile(e.target.files?.[0])}
                        className="rounded-sm mt-1"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Mode</Label>
                        <Select value={uploadForm.mode} onValueChange={(v) => setUploadForm({ ...uploadForm, mode: v })}>
                          <SelectTrigger className="rounded-sm mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="header_only">Header Only</SelectItem>
                            <SelectItem value="full_page">Full Page</SelectItem>
                            <SelectItem value="watermark">Watermark</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Page Size</Label>
                        <Select value={uploadForm.page_size} onValueChange={(v) => setUploadForm({ ...uploadForm, page_size: v })}>
                          <SelectTrigger className="rounded-sm mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="A4">A4</SelectItem>
                            <SelectItem value="Letter">Letter</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Header Height (mm)</Label>
                        <Input
                          type="number"
                          value={uploadForm.header_height}
                          onChange={(e) => setUploadForm({ ...uploadForm, header_height: parseInt(e.target.value) || 30 })}
                          className="rounded-sm mt-1"
                        />
                      </div>
                      <div className="flex items-end gap-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="is_default"
                            checked={uploadForm.is_default}
                            onChange={(e) => setUploadForm({ ...uploadForm, is_default: e.target.checked })}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="is_default" className="text-sm">Set as default</Label>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button variant="outline" onClick={() => setUploadDialogOpen(false)} className="flex-1 rounded-sm">
                        Cancel
                      </Button>
                      <Button onClick={handleUploadLetterhead} className="flex-1 rounded-sm bg-blue-600 hover:bg-blue-700">
                        Upload
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="text-center py-12 text-slate-400">Loading...</div>
            ) : letterheads.length === 0 ? (
              <Card className="border-slate-200 rounded-sm">
                <CardContent className="py-12 text-center">
                  <Image className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No letterheads uploaded yet</p>
                  <Button 
                    onClick={() => setUploadDialogOpen(true)} 
                    variant="outline" 
                    className="mt-4 rounded-sm"
                  >
                    Upload Your First Letterhead
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {letterheads.map((lh) => (
                  <Card key={lh.id} className="border-slate-200 rounded-sm hover:shadow-md transition-shadow" data-testid={`letterhead-card-${lh.id}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-semibold text-slate-900">{lh.name}</h3>
                          <p className="text-xs text-slate-400">{lh.original_filename}</p>
                        </div>
                        {lh.is_default && (
                          <Badge className="bg-blue-100 text-blue-700 rounded-sm">Default</Badge>
                        )}
                      </div>
                      
                      <div className="flex gap-2 text-xs text-slate-500 mb-3">
                        <span className="bg-slate-100 px-2 py-1 rounded-sm">{lh.page_size}</span>
                        <span className="bg-slate-100 px-2 py-1 rounded-sm">{lh.mode.replace('_', ' ')}</span>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 rounded-sm"
                          onClick={() => window.open(`${api.defaults.baseURL}/documents/letterheads/${lh.id}/preview`, '_blank')}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Preview
                        </Button>
                        {!lh.is_default && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-sm"
                            onClick={() => handleSetDefault(lh.id)}
                          >
                            Set Default
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50 rounded-sm"
                          onClick={() => handleDeleteLetterhead(lh.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default DocumentEngine;
